import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

const SAVED_STATUS_TIMEOUT = 2000;
const EMAIL_VALIDATE_DEBOUNCE = 750;

/**
 * @typedef {object} CartGuestProgrammesRefs
 * @property {HTMLElement} status
 */

/**
 * Per-line cart editor for guest programme selections (plus optional name + email when enabled).
 * Persists changes via /cart/change.js with full properties replacement and race protection.
 *
 * @extends Component<CartGuestProgrammesRefs>
 */
class CartGuestProgrammesComponent extends Component {
  requiredRefs = ['status'];

  /** @type {AbortController | null} */
  #inFlight = null;
  #latestRequestId = 0;
  /** @type {number | undefined} */
  #statusTimeout;
  /** @type {number | undefined} */
  #emailValidateTimeout;
  /** @type {Set<string>} */
  #announcedDuplicates = new Set();

  #lifecycle = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#lifecycle;
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('input', this.#onInput, { signal });
    this.addEventListener('invalid', this.#onInvalid, { signal, capture: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#lifecycle.abort();
    this.#inFlight?.abort();
    if (this.#statusTimeout) clearTimeout(this.#statusTimeout);
    if (this.#emailValidateTimeout) clearTimeout(this.#emailValidateTimeout);
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!this.#isGuestField(target)) return;
    // Selects fire change on commit. Text/email inputs fire change on blur.
    this.#save(target);
  };

  /** @param {Event} event */
  #onInput = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.dataset.guestEmail === undefined) return;
    // Debounce duplicate-email checks so the live region doesn't fire mid-typing.
    if (this.#emailValidateTimeout) clearTimeout(this.#emailValidateTimeout);
    this.#emailValidateTimeout = window.setTimeout(() => {
      this.#validateEmailUniqueness();
    }, EMAIL_VALIDATE_DEBOUNCE);
  };

  /** @param {Event} event */
  #onInvalid = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement)) return;
    if (!target.dataset.guestProperty) return;
    const number = target.dataset.guestIndex || '';
    const template = this.dataset.invalidAnnouncementTemplate || '';
    this.#announce(template.replace('[number]', number));
  };

  /**
   * @param {EventTarget | null} target
   * @returns {target is (HTMLSelectElement | HTMLInputElement)}
   */
  #isGuestField(target) {
    if (!(target instanceof HTMLSelectElement) && !(target instanceof HTMLInputElement)) {
      return false;
    }
    return Boolean(target.dataset.guestProperty);
  }

  /** @param {HTMLSelectElement | HTMLInputElement} changedField */
  #save(changedField) {
    const lineKey = this.dataset.lineKey;
    if (!lineKey) return;

    this.#latestRequestId++;
    const myRequestId = this.#latestRequestId;

    this.#inFlight?.abort();
    this.#inFlight = new AbortController();

    const previousValue = changedField.dataset.previousValue ?? '';
    changedField.dataset.previousValue = changedField.value;

    const activeId = document.activeElement instanceof HTMLElement ? document.activeElement.id : null;

    this.#showStatus(this.dataset.saving || '', false);
    this.#announce(this.dataset.savingAnnouncement || '');

    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sectionIds.add(el.dataset.sectionId);
      }
    });

    const state = this.#buildState();
    const body = JSON.stringify({
      id: lineKey,
      properties: state.properties,
      sections: Array.from(sectionIds).join(','),
      sections_url: window.location.pathname,
    });

    fetch(`${Theme.routes.cart_change_url}`, {
      ...fetchConfig('json', { body }),
      signal: this.#inFlight.signal,
    })
      .then((response) => response.text())
      .then((responseText) => {
        if (myRequestId !== this.#latestRequestId) return;

        const parsed = JSON.parse(responseText);

        if (parsed.errors || parsed.status) {
          this.#revert(changedField, previousValue);
          const detail = this.#extractErrorDetail(parsed);
          this.#showStatus(
            this.#combineErrorDetail(this.dataset.saveError || '', detail),
            true
          );
          this.#announce(
            this.#combineErrorDetail(this.dataset.saveErrorAnnouncement || '', detail)
          );
          return;
        }

        sectionIds.forEach((sid) => {
          const html = parsed.sections?.[sid];
          if (html) morphSection(sid, html);
        });

        if (activeId) {
          const restored = document.getElementById(activeId);
          if (restored instanceof HTMLElement) {
            restored.focus({ preventScroll: true });
          }
        }

        this.dispatchEvent(
          new CartUpdateEvent(parsed, this.dataset.sectionId || '', {
            source: 'cart-guest-programmes-component',
            sections: parsed.sections,
          })
        );

        this.dispatchEvent(
          new CustomEvent('cart-guests-updated', {
            bubbles: true,
            detail: {
              lineKey,
              productTitle: this.dataset.productTitle || '',
              guests: state.guests,
            },
          })
        );

        this.#showStatus(this.dataset.saved || '', false);
        this.#announce(this.dataset.savedAnnouncement || '');
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        if (myRequestId !== this.#latestRequestId) return;
        this.#revert(changedField, previousValue);
        this.#showStatus(this.dataset.saveError || '', true);
        this.#announce(this.dataset.saveErrorAnnouncement || '');
        console.error(error);
      });
  }

  /**
   * @param {HTMLSelectElement | HTMLInputElement} field
   * @param {string} previousValue
   */
  #revert(field, previousValue) {
    field.value = previousValue;
    field.dataset.previousValue = previousValue;
  }

  /** @returns {Record<string, string>} */
  #buildPropertiesPayload() {
    const { properties, guests, total } = this.#buildState();

    return properties;
  }

  /**
   * Snapshot the current state: per-property strings for Shopify, plus a structured guests array
   * for downstream consumers (guest management system, analytics).
   *
   * @returns {{ properties: Record<string,string>, guests: Array<{guest:number,programme:string,firstName:string,lastName:string,email:string}>, total: number }}
   */
  #buildState() {
    /** @type {Record<string, string>} */
    const properties = {};
    /** @type {Map<string, number>} */
    const tally = new Map();
    /** @type {Map<number, {guest:number,programme:string,firstName:string,lastName:string,email:string}>} */
    const guestsByIndex = new Map();
    let total = 0;

    const fields = this.querySelectorAll('[data-guest-property]');
    fields.forEach((el) => {
      if (!(el instanceof HTMLSelectElement) && !(el instanceof HTMLInputElement)) return;
      const key = el.dataset.guestProperty;
      if (!key) return;
      const value = el.value.trim();
      properties[key] = value;

      const idx = Number(el.dataset.guestIndex) || 0;
      if (idx > 0) {
        if (!guestsByIndex.has(idx)) {
          guestsByIndex.set(idx, {
            guest: idx,
            programme: '',
            firstName: '',
            lastName: '',
            email: '',
          });
        }
        const guest = /** @type {{programme:string,firstName:string,lastName:string,email:string}} */ (
          guestsByIndex.get(idx)
        );
        if (key.endsWith(' Programme')) guest.programme = value;
        else if (key.endsWith(' First Name')) guest.firstName = value;
        else if (key.endsWith(' Last Name')) guest.lastName = value;
        else if (key.endsWith(' Email')) guest.email = value;
      }

      if (key.endsWith(' Programme') && value) {
        tally.set(value, (tally.get(value) || 0) + 1);
        total += 1;
      }
    });

    tally.forEach((count, programme) => {
      properties[`_${programme} count`] = String(count);
    });
    if (total > 0) {
      properties['_Total guests'] = String(total);
    }

    const guests = Array.from(guestsByIndex.keys())
      .sort((a, b) => a - b)
      .map((i) => /** @type {any} */ (guestsByIndex.get(i)));

    // Structured machine-readable aggregate. Hidden from cart UI (leading underscore),
    // surfaces in webhooks/order export/Shopify Flow as line_item.properties._Guests.
    properties['_Guests'] = JSON.stringify(guests);

    return { properties, guests, total };
  }

  #validateEmailUniqueness() {
    const emailInputs = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('input[data-guest-email]')
    );
    if (emailInputs.length === 0) return;

    /** @type {Map<string, HTMLInputElement[]>} */
    const byNormalised = new Map();
    emailInputs.forEach((input) => {
      const value = input.value.trim().toLowerCase();
      if (!value) {
        // Clear any prior duplicate state if user emptied the field.
        input.setCustomValidity('');
        this.#showEmailError(input, '');
        return;
      }
      const list = byNormalised.get(value) || [];
      list.push(input);
      byNormalised.set(value, list);
    });

    const duplicateMessage = this.dataset.emailDuplicateMessage || 'Each guest must have a unique email';

    byNormalised.forEach((inputs) => {
      if (inputs.length < 2) {
        // Unique — clear any error left over from a previous check.
        inputs.forEach((input) => {
          input.setCustomValidity('');
          this.#showEmailError(input, '');
        });
        return;
      }

      // Mark all duplicates as invalid and surface the inline message.
      inputs.forEach((input) => {
        input.setCustomValidity(duplicateMessage);
        this.#showEmailError(input, duplicateMessage);
      });

      // Announce once per duplicate group per debounce cycle.
      const sortedIndices = inputs
        .map((input) => Number(input.dataset.guestIndex) || 0)
        .sort((a, b) => a - b);
      const announceKey = sortedIndices.join(',');
      if (!this.#announcedDuplicates.has(announceKey)) {
        const template = this.dataset.emailDuplicateAnnouncementTemplate || '';
        const latest = sortedIndices[sortedIndices.length - 1];
        const earliest = sortedIndices[0];
        const message = template.replace('[number]', String(latest)).replace('[other]', String(earliest));
        this.#announce(message);
        this.#announcedDuplicates.add(announceKey);
      }
    });

    // Reset announce-once tracking for groups that are no longer duplicated.
    const activeKeys = new Set();
    byNormalised.forEach((inputs) => {
      if (inputs.length >= 2) {
        const sortedIndices = inputs.map((i) => Number(i.dataset.guestIndex) || 0).sort((a, b) => a - b);
        activeKeys.add(sortedIndices.join(','));
      }
    });
    this.#announcedDuplicates.forEach((key) => {
      if (!activeKeys.has(key)) this.#announcedDuplicates.delete(key);
    });
  }

  /**
   * @param {HTMLInputElement} input
   * @param {string} message
   */
  #showEmailError(input, message) {
    const errorId = input.getAttribute('aria-describedby');
    if (!errorId) return;
    const errorEl = this.querySelector(`#${CSS.escape(errorId)}`);
    if (!(errorEl instanceof HTMLElement)) return;
    if (message) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    } else {
      errorEl.textContent = '';
      errorEl.hidden = true;
    }
  }

  /**
   * @param {string} text
   * @param {boolean} isError
   */
  #showStatus(text, isError) {
    const { status } = this.refs;
    if (!status) return;

    if (this.#statusTimeout) {
      clearTimeout(this.#statusTimeout);
      this.#statusTimeout = undefined;
    }

    status.textContent = text;
    status.classList.toggle('cart-guest-programmes__status--error', isError);
    status.classList.toggle('cart-guest-programmes__status--visible', text !== '');

    if (!isError && text !== '') {
      this.#statusTimeout = window.setTimeout(() => {
        status.textContent = '';
        status.classList.remove('cart-guest-programmes__status--visible');
      }, SAVED_STATUS_TIMEOUT);
    }
  }

  /** @param {object} parsed */
  #extractErrorDetail(parsed) {
    if (typeof parsed.description === 'string') return parsed.description;
    if (typeof parsed.message === 'string') return parsed.message;
    if (typeof parsed.errors === 'string') return parsed.errors;
    if (Array.isArray(parsed.errors)) return parsed.errors.join(', ');
    if (parsed.errors && typeof parsed.errors === 'object') {
      return Object.entries(parsed.errors)
        .map(([key, val]) => `${key}: ${Array.isArray(val) ? val.join(', ') : val}`)
        .join('; ');
    }
    return '';
  }

  /**
   * @param {string} prefix
   * @param {string} detail
   */
  #combineErrorDetail(prefix, detail) {
    if (!detail) return prefix;
    if (!prefix) return detail;
    return `${prefix}: ${detail}`;
  }

  /** @param {string} text */
  #announce(text) {
    if (!text) return;
    const region = this.closest('form')?.querySelector('[data-cart-guest-programmes-live]');
    if (region instanceof HTMLElement) {
      region.textContent = text;
    }
  }
}

if (!customElements.get('cart-guest-programmes-component')) {
  customElements.define('cart-guest-programmes-component', CartGuestProgrammesComponent);
}
